import { Box, Button, Dialog, DialogActions, MenuItem, Select, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { AuthState } from "context/AuthContext";
import { PimDashboardState } from "context/PimDashboardContext";
import { useEffect, useMemo, useState } from "react";
import { inputTypes } from "data/globalData";
import {
	reqColorLevelColsForValueAddBreakdown,
	valueAddConsumptionAssociationObj,
	valueAddConsumptionTableColumns,
	valueAddTableColumns,
	breakdownGridRowIdKey,
	breakdownRowUpdatedKey,
	normalizeValueAddBreakdownRowsForGrid,
	isTruthyBreakdownLevel,
	getValueAddItemConsumptionRows,
} from "../../data";
import { WhiteBgBox } from "../../TrimsSection/TrimsSection";
import BreakdownDataGrid from "../../BreakdownDataGrid";

const colStyle = {
	headerAlign: "center",
	align: "center",
};

const ValueAddBreakdown = ({ open, onClose, selectedValueAddItem, onUpdateValueAddItem, isEditMode = false, placementOptions = [] }) => {
	const { allAttributes } = PimDashboardState();
	const { user } = AuthState();
	const [breakdownRows, setBreakdownRows] = useState([]);
	const valueAddColorLevelKey = valueAddTableColumns.color_level.key;
	const valueAddSizeLevelKey = valueAddTableColumns.size_level.key;
	const valueAddChannelLevelKey = valueAddTableColumns.channel_level.key;
	const placementKey = valueAddConsumptionTableColumns.placement.key;

	const getBreakdownRowKey = (row) => row?.[breakdownGridRowIdKey];

	const syncBreakdownRows = (rows) => normalizeValueAddBreakdownRowsForGrid(rows, { prefix: "va-bd" });

	const updateBreakdownRow = (updatedRow) => {
		setBreakdownRows((prev) => {
			const updatedKey = getBreakdownRowKey(updatedRow);
			const nextRows = syncBreakdownRows(
				prev.map((row) =>
					getBreakdownRowKey(row) === updatedKey
						? {
								...updatedRow,
								...(updatedRow?.id ? { [breakdownRowUpdatedKey]: true } : {}),
								updated_by: user?.email,
							}
						: row,
				),
			);
			onUpdateValueAddItem?.({ ...selectedValueAddItem, [valueAddConsumptionAssociationObj]: nextRows });
			return nextRows;
		});
		return updatedRow;
	};

	const breakdownColumns = useMemo(() => {
		const baseColumns = [
			{
				...valueAddConsumptionTableColumns.category,
				flex: 1.1,
				minWidth: 110,
				...colStyle,
				headerName: valueAddConsumptionTableColumns.category.name,
				field: valueAddConsumptionTableColumns.category.key,
			},
			{
				...valueAddConsumptionTableColumns.description,
				flex: 1.4,
				minWidth: 130,
				...colStyle,
				headerName: valueAddConsumptionTableColumns.description.name,
				field: valueAddConsumptionTableColumns.description.key,
			},
		];

		const colorLevelColumns = isTruthyBreakdownLevel(selectedValueAddItem?.[valueAddColorLevelKey])
			? allAttributes
					?.filter((row) => reqColorLevelColsForValueAddBreakdown.includes(row?.key))
					.map((row) => ({
						...row,
						flex: 0.9,
						minWidth: 110,
						...colStyle,
						headerName: row.name,
						field: row.key,
					}))
			: [];

		const sizeLevelColumns = isTruthyBreakdownLevel(selectedValueAddItem?.[valueAddSizeLevelKey])
			? [
					{
						...valueAddConsumptionTableColumns.size,
						flex: 0.8,
						minWidth: 110,
						...colStyle,
						headerName: valueAddConsumptionTableColumns.size.name,
						field: valueAddConsumptionTableColumns.size.key,
					},
				]
			: [];

		const channelLevelColumns = isTruthyBreakdownLevel(selectedValueAddItem?.[valueAddChannelLevelKey])
			? [
					{
						...valueAddConsumptionTableColumns.channel,
						flex: 0.9,
						minWidth: 110,
						...colStyle,
						headerName: valueAddConsumptionTableColumns.channel.name,
						field: valueAddConsumptionTableColumns.channel.key,
					},
				]
			: [];

		const detailColumns = [
			{
				...valueAddConsumptionTableColumns.placement,
				flex: 1.1,
				minWidth: 150,
				...colStyle,
				headerName: valueAddConsumptionTableColumns.placement.name,
				field: placementKey,
				renderCell: ({ row }) =>
					isEditMode ? (
						<Box width="100%" height="100%" display="flex" alignItems="center" px={0.5} boxSizing="border-box">
							<Select
								size="small"
								fullWidth
								value={row?.[placementKey] && row[placementKey] !== inputTypes.SELECT.key ? row[placementKey] : inputTypes.SELECT.key}
								displayEmpty
								onChange={(e) => updateBreakdownRow({ ...row, [placementKey]: e.target.value })}
								MenuProps={{ PaperProps: { sx: { maxHeight: 220 } } }}>
								<MenuItem value={inputTypes.SELECT.key}>
									<span style={{ color: grey[400] }}>Select Placement</span>
								</MenuItem>
								{placementOptions.map((option) => (
									<MenuItem key={option} value={option}>
										{option}
									</MenuItem>
								))}
							</Select>
						</Box>
					) : (
						<WhiteBgBox field={placementKey} row={row} />
					),
			},
		];

		return [...baseColumns, ...colorLevelColumns, ...sizeLevelColumns, ...channelLevelColumns, ...detailColumns];
	}, [allAttributes, selectedValueAddItem, valueAddColorLevelKey, valueAddSizeLevelKey, valueAddChannelLevelKey, isEditMode, placementOptions, breakdownRows]);

	useEffect(() => {
		const existingRows = getValueAddItemConsumptionRows(selectedValueAddItem);
		setBreakdownRows(syncBreakdownRows(existingRows?.length ? existingRows : []));
	}, [selectedValueAddItem]);

	const subtitle = [
		selectedValueAddItem?.[valueAddConsumptionTableColumns.category.key] || "Value Add",
		selectedValueAddItem?.[valueAddConsumptionTableColumns.description.key]
			? ` - ${selectedValueAddItem[valueAddConsumptionTableColumns.description.key]}`
			: "",
	]
		.filter(Boolean)
		.join("");

	return (
		<Dialog
			onClose={onClose}
			open={open}
			fullWidth
			maxWidth="lg"
			PaperProps={{
				sx: {
					borderRadius: 3,
					overflow: "hidden",
					border: `1px solid ${grey[300]}`,
					backgroundColor: "#fff",
				},
			}}>
			<Box
				sx={{
					background: `linear-gradient(135deg, ${grey[50]} 0%, #ffffff 55%, ${grey[50]} 100%)`,
					borderBottom: `1px solid ${grey[300]}`,
					px: 3,
					py: 2,
					boxSizing: "border-box",
				}}>
				<Stack spacing={1}>
					<Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
						<Box>
							<Typography variant="h6" sx={{ fontWeight: 800, color: grey[900], lineHeight: 1.15 }}>
								Breakdown
							</Typography>
							<Typography variant="body2" sx={{ color: grey[600], mt: 0.4 }}>
								{subtitle}
							</Typography>
						</Box>
					</Box>
				</Stack>
			</Box>

			<Box width="100%" p={2} sx={{ pt: 1.5, overflow: "hidden", boxSizing: "border-box" }}>
				<Box
					sx={{
						border: `1px solid ${grey[300]}`,
						borderRadius: 2,
						overflow: "hidden",
						backgroundColor: "#fff",
						boxSizing: "border-box",
					}}>
					<BreakdownDataGrid columns={breakdownColumns} rows={breakdownRows} />
				</Box>
			</Box>

			<DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
				<Button variant="outlined" onClick={onClose} sx={{ textTransform: "none", fontWeight: 700 }}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ValueAddBreakdown;
